
import TraversTree from './Helpers/TreeTravers.js'

export default {
    name: 'PageContent',
    props: {
        pageContent: {
            type: String,
            default: () => null
        },
        source: {
            type: Object,
            default: () => null
        },
        highlights: {
            type: Array,
            default: () => null
        },
        isVisible: Boolean
    },
    inject: ['user'],
    computed: {
        content () {
            if (!this.pageContent || !this.pageContent.trim().length) {
                return ''
            }
            let content = this.setUpTable(this.pageContent)
            content = this.setUpHighlights(content)

            return content
        }
    },
    methods: {
        getClass (color) {
            return [{
                color: 'redHighlight',
                class: 'bg-red-200/[0.40]'
            }, {
                color: 'yellowHighlight',
                class: 'bg-yellow-200/[0.40]'
            }, {
                color: 'blueHighlight',
                class: 'bg-blue-200/[0.40]'
            }, {
                color: 'greenHighlight',
                class: 'bg-green-200/[0.40]'
            }, {
                color: 'purbleHighlight',
                class: 'bg-purple-200/[0.40]'
            }].find(c => c.color === color).class
        },
        getBorderClass (color) {
            return [{
                color: 'redHighlight',
                class: 'border-red-600'
            }, {
                color: 'yellowHighlight',
                class: 'border-yellow-600'
            }, {
                color: 'blueHighlight',
                class: 'border-brand-500'
            }, {
                color: 'greenHighlight',
                class: 'border-green-600'
            }, {
                color: 'purbleHighlight',
                class: 'border-purple-600'
            }].find(c => c.color === color).class
        },
        setUpHighlights (content) {
            if (!this.highlights || !this.highlights.length || !this.isVisible) {
                return content
            }

            const pageHighlights = this.highlights.filter(h => h.locale === this.source.locale)

            const dom = document.createElement('div')
            dom.innerHTML = content
            const highlights = [...pageHighlights]

            highlights.forEach((h) => {
                const start = parseInt(h.start_id)
                let end = parseInt(h.end_id)
                if (start === end) {
                    end += 1
                }
                h.start = Math.min(start, end)
                h.end = Math.max(start, end)
            })
            highlights.sort((a, b) => {
                if (a.start < b.start) {
                    return -1
                }
                if (a.start > b.start) {
                    return 1
                }
                return 0
            })
            const underlineProgress = new Map()

            TraversTree.walkInTree((el, startOffset) => {
                if (el.parentNode.nodeName === 'MARK' || el.parentNode.nodeName === 'MARK_UNDERLINE') {
                    return
                }
                const textContent = el.textContent
                const endOffset = startOffset + textContent.length
                for (const h of highlights) {
                    let startFromCurrentElement = 0
                    let length = 0
                    if (startOffset <= h.start && h.start < endOffset) {
                        startFromCurrentElement = h.start - startOffset
                        length = h.end - h.start
                    } else if (startOffset < h.end && h.end <= endOffset) {
                        startFromCurrentElement = 0
                        length = h.end - startOffset
                    } else if (h.start <= startOffset && endOffset < h.end) {
                        startFromCurrentElement = 0
                        length = endOffset - startOffset
                    } else {
                        continue
                    }

                    const highlightedText = textContent.substring(startFromCurrentElement, (startFromCurrentElement + length))
                    let before = textContent.substring(0, startFromCurrentElement)
                    let after = textContent.substring((startFromCurrentElement + length), endOffset)

                    const mark = document.createElement('mark')
                    if (h.note) {
                        const words = highlightedText.match(/\S+\s*/g) || []
                        const currentCount = underlineProgress.get(h.id) || 0

                        const wordsToUnderlineCount = Math.min(3 - currentCount, words.length)

                        if (wordsToUnderlineCount > 0) {
                            const underlinedText = words.slice(0, wordsToUnderlineCount).join('')
                            const remainingText = words.slice(wordsToUnderlineCount).join('')

                            underlineProgress.set(h.id, currentCount + wordsToUnderlineCount)

                            const markUnderline = document.createElement('mark_underline')
                            markUnderline.textContent = underlinedText
                            markUnderline.classList.add('border-b-[2px]', 'py-1', this.getBorderClass(h.color_class))
                            mark.appendChild(markUnderline)

                            if (remainingText) {
                                mark.appendChild(document.createTextNode(remainingText))
                            }
                        } else {
                            mark.appendChild(document.createTextNode(highlightedText))
                        }
                    } else {
                        mark.appendChild(document.createTextNode(highlightedText))
                    }
                    mark.classList.add(this.getClass(h.color_class), 'py-1')
                    mark.setAttribute('data-highlight-id', h.id)
                    mark.setAttribute('data-page-id', h.page_id)
                    if (el.parentNode.nodeName === 'A') {
                        mark.classList.add('text-brand-500')
                        mark.classList.add('underline')
                    }

                    if (before.length) {
                        before = document.createTextNode(before)
                        el.parentNode.insertBefore(before, el)
                    }
                    el.parentNode.insertBefore(mark, el)
                    if (after.length) {
                        after = document.createTextNode(after)
                        el.parentNode.insertBefore(after, el)
                    }
                    el.parentNode.removeChild(el)

                    return typeof before === 'object' ? before : mark
                }
            }, dom, '#text')

            return dom.innerHTML
        },
        setUpTable (content) {
            if (content.includes('table') === false) {
                return content
            }

            const dom = document.createElement('div')
            dom.innerHTML = content
            Array.from(dom.getElementsByTagName('table')).forEach((table) => {
                const newTable = document.createElement('div')
                newTable.dataset.tableContainer = true
                newTable.innerHTML = table.outerHTML
                table.replaceWith(newTable)
            })

            return dom.innerHTML
        }
    }
}
